<template lang="pug">
    v-card
        v-card-title Vin Solutions Login
        template(v-if="!isLoggingIn")
            template(v-if="vinLoginStatus == 'unenabled'")
                v-card-text
                    p Vin Solutions Integration is not enabled for your account. Your manager can enable it in the Users admin page.
            template(v-else-if="vinLoginStatus == 'online'")
                v-card-text.text-center
                    v-icon.text-center(color="success" size="100") mdi-check-circle
                    p Vin Solutions Integration is enabled for your account and you are logged in. No further action is needed.
            template(v-else-if="newMfaCodeRequired")
                v-card-text
                    p It looks like we need new credentials or a new MFA code to continue with your Vin Solutions integration. Please click the Login button below to get started.
            template(v-else-if="timeoutOrError")
                v-card-text
                    p Something unexpected happened when logging you into Vin Solutions, please try logging in again.
                    p If the problem persists, please contact Volie support.
            template(v-else-if="!mfaRequired")
                v-card-text
                    p Please login with your Vin Solutions credentials to enable Vin Solutions Integrations.
                    p If MFA/2FA is required, you will be prompted to enter your MFA code after clicking the Login button.
                    v-alert(v-if="failedLogin" type="error" outlined) Something went wrong when logging you into Vin Solutions (invalid username or password, MFA timed out, account locked), please try logging in again.
                    v-form(autocomplete="off" ref="loginForm")
                        v-row
                            v-col(cols="12")
                                v-text-field(
                                    v-model="loginForm.username"
                                    label="Vin Solutions Username"
                                    required
                                    autocomplete="new-password"
                                )
                                v-text-field(
                                    v-model="loginForm.password"
                                    label="Vin Solutions Password"
                                    required
                                    type="password"
                                    autocomplete="new-password"
                                )
            template(v-else-if="mfaRequired")
                v-card-text
                    p Please enter your MFA code.
                    v-alert(v-if="failedMfa" type="error" outlined) Invalid MFA code
                    v-form(autocomplete="off" ref="mfaForm")
                        v-row
                            v-col(cols="12")
                                v-text-field(
                                    v-model="loginForm.mfa_code"
                                    label="MFA Code"
                                    required
                                    autocomplete="new-password"
                                )

            v-card-actions
                v-spacer
                v-btn(ripple outlined color="primary" @click="$emit('close-dialog'); isLoggingIn = false;")
                    v-icon mdi-close
                    | Close
                v-btn.ml-2(v-if="renderLoginButton" ripple color="success" @click="handleSubmit()" :disabled="loginButtonDisabled")
                    v-icon mdi-login
                    | Login

        template(v-else)
            v-card-text.text-center
                .loading-container.py-4
                    span.text-subtitle-1.mb-4.d-block {{ currentStepText }}
                    v-progress-linear(
                        indeterminate
                        color="primary"
                        rounded
                        height="4"
                        class="mx-auto"
                        style="max-width: 240px"
                    )

            v-card-actions
                v-spacer
                v-btn(ripple outlined color="primary" @click="$emit('close-dialog');")
                    v-icon mdi-close
                    | Close
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Components

// Data
import { GetIntegrationUser, CreateIntegrationUser, IntegrationsLogin, IntegrationsSubmitMfa } from '@/data';

export default Vue.extend({
    data() {
        return {
            authStatus: null,
            bus: VolieState.websocketClient().eventBus(),
            createUser: false,
            failedLogin: false,
            failedMfa: false,
            isLoggingIn: false,
            loginForm: {
                username: '',
                password: '',
                mfa_code: '',
            },
            mfaRequired: false,
            newMfaCodeRequired: false,
            steps: [
                {
                    key: "auth.login_attempt",
                    value: "Logging you in..."
                },
                {
                    key: "auth.mfa_verification",
                    value: "Checking if MFA is required..."
                },
                {
                    key: "mfa_submitted",
                    value: "Submitting your MFA code..."
                },
                {
                    key: "auth.success",
                    value: "You're logged in! Vin Solutions will receive actions from your account."
                },
            ],
            timeoutOrError: false,
        }
    },
    methods: {
        createIntegrationUser() {
            const options = {
                username: this.loginForm.username,
                password: this.loginForm.password,
                skipLogin: false,
                update_user: this.failedLogin,
            }

            this.isLoggingIn = true;
            CreateIntegrationUser(options).then((response) => {
                if (response.Data?.status !== 200 || response.Error) {
                    let err = "Something went wrong, please try again later";
                    if (response.Error) {
                        err = response.Error;
                    }

                    this.showSnackbar("error", err);
                    this.isLoggingIn = false;
                } else {
                    this.createUser = false;
                }
            });
        },
        getIntegrationUser() {
            // Check if the user is actually enabled for Vin Solutions
            if (!this.currentUser?.need_vin_solutions_auth || !this.currentUser?.vin_login_enabled) {
                return;
            }

            // Uses ctx to check if the user is logged in for Integration App, no need to pass anything
            GetIntegrationUser().then((response) => {
                if (response.Error) {
                    console.error("getIntegrationUser: ", response.Error);
                    return;
                }

                if (response.Data?.user?.has_active_session) {
                    return;
                }

                // Check if the user isn't found in Vin and create one
                if (response.Data?.user?.message == "User not found") {
                    this.createUser = true;
                    this.dialog = this.renderDialog;
                    return;
                }

                if (response.Data?.user?.logins?.length > 0) {
                    var login = response.Data?.user?.logins[0];

                    // If user has a login object, check if it's in a successful state or not
                    if (login.login_successful) {
                        return;
                    } else {
                        this.failedLogin = true;
                        this.createUser = true;
                        this.dialog = this.renderDialog;
                    }
                } else {
                    this.dialog = this.renderDialog;
                }
            });
        },
        getStepValue() {
            if (!this.authStatus) {
                return "Please wait while we log you in...";
            }
            
            const step = this.steps.find(step => step.key === this.authStatus);
            return step ? step.value : "Please wait while we log you in...";
        },
        handleLogin() {
            const options = {
                username: this.loginForm.username.trim() || "",
                password: this.loginForm.password.trim() || "",
            }

            this.isLoggingIn = true;
            
            // no longer need these toggled, we're now in the normalized login flow
            this.newMfaCodeRequired = false;
            this.timeoutOrError = false;
            
            // If successful, we'll run off the webhooks rather than further responses from the integration app
            IntegrationsLogin(options).then((response) => {
                if (response.Data?.status !== 200 || response.Error) {
                    switch (response.Error) {
                        case "user is already logging into vin solutions":
                            // Do nothing, waiting for integration app to finish logging in
                            break;
                        default:
                            this.showSnackbar("error", "Something went wrong when logging you into Vin Solutions. Please contact Volie support if the problem persists.");
                            this.isLoggingIn = false;
                            break;
                    }
                }
            });
        },
        handleMfa() {
            const options = {
                mfa_code: this.loginForm.mfa_code.trim(),
            }

            this.isLoggingIn = true;
            this.authStatus = "mfa_submitted";
            this.loginForm.mfa_code = '';
            
            // Still running with webhooks, only worry about integration app responses if we get an error
            IntegrationsSubmitMfa(options).then((response) => {
                if (response.Data.status !== 200 || response.Error) {
                    let err = "Something went wrong, please try again later";
                    if (response.Error) {
                        err = response.Error;
                    }

                    this.showSnackbar("error", err);
                    this.isLoggingIn = false;
                }
            });
        },
        handleSubmit() {
            if (this.loginForm.mfa_code) {
                this.handleMfa();
            } else if (this.createUser) {
                this.createIntegrationUser();
            } else {
                this.handleLogin();
            }
        },
        onAuthEventNew(event) {
            // Verify response
            if (!event) {
                return;
            }

            switch(event) {
                case "auth.login_attempt":
                    this.authStatus = event;
                    break;
                case "auth.mfa_verification":
                    this.authStatus = event;
                    break;
                case "auth.mfa_required":
                    this.dialog = this.renderDialog;
                    this.isLoggingIn = false;
                    this.mfaRequired = true;
                    break;
                case "auth.failure":
                    this.resetLoginState();
                    this.createUser = true;
                    this.failedLogin = true;
                    break;
                case "auth.success":
                    this.authStatus = event; // TODO: a nice UX would be giving a green checkmark in the login component before closing, then we dont have to use snackbar
                    this.resetLoginState();

                    // temp bandaid to only show the success message if we went through the login process
                    if (this.dialog) {
                        this.dialog = false;
                        this.showSnackbar("success", "Login successful - Vin Solutions will receive actions from your account");
                    }
                    break;
                case "auth.timeout":
                    this.resetLoginState();
                    this.timeoutOrError = true;
                    break;
                case "auth.error":
                    this.resetLoginState();
                    this.timeoutOrError = true;
                case "session.started":
                    break;
                case "session.ended":
                    break;
                default:
                    console.warn("onAuthEventNew: Something unexpected happened: ", event);
                    break;
            }
        },
        resetLoginState() {
            this.failedLogin = false;
            this.failedMfa = false;
            this.isLoggingIn = false;
            this.mfaRequired = false;
            this.timeoutOrError = false;
        },
    },
    computed: {
        currentStepText() {
            if (!this.authStatus) {
                return "Logging you in...";
            }

            const step = this.steps.find(step => step.key === this.authStatus);
            return step ? step.value : "Logging you in...";
        },
        loginButtonDisabled() {
            let loginDisabled = true;

            if (!this.mfaRequired && this.loginForm.username !== "" && this.loginForm.password !== "") {
                loginDisabled = false;
            }

            if (this.mfaRequired && this.loginForm.mfa_code !== "") {
                loginDisabled = false;
            }

            if (this.newMfaCodeRequired) {
                loginDisabled = false;
            }

            if (this.isLoggingIn) {
                loginDisabled = true;
            }

            return loginDisabled;
        },
        renderDialog() {
            return this.currentUser?.vin_login_enabled;
        },
        renderLoginButton() {
            return !(this.vinLoginStatus == "unenabled" || this.vinLoginStatus == "online");
        }
    },
    components: {

    },
    props: {
        authEvent: String,
        currentUser: Object,
        showSnackbar: Function,
        vinLoginStatus: String,
    },
    watch: {
        authEvent(newVal) {
            if (newVal) {
                this.onAuthEventNew(newVal);
            }
        }
    },
    created() {
        this.getIntegrationUser();
    },
    destroyed() {
    },
    mounted() {
    },
})
</script>

<style lang="scss" scoped>
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
</style>